import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { Classification } from "routes/schedule/tasks/classification"
import { VALID_CLASSIFICATIONS } from "library/SFG20/constants.mjs"
import Iconify from "minimals-template/components/Iconify"
import { HighlightBox } from "lib/@components/highlightBox"
import { linkButtonSx, linkedButtonSx, unlinkButtonPositionSx } from "./component-link-button-styles"
import { preventPropagation } from "lib/prevent"

const FIELD = "classification"

/**
 * Slot plug for ClassificationArea.
 *
 * - No componentRef: row-level button handles selection (see ComponentRefRowLink)
 * - ComponentRef set, field not linked: puzzle icon toggles field on (if component has value)
 * - Field linked: shows inherited classification + link icon to unlink
 */
export function ComponentRefClassification({ originalchildren }) {
    const { componentRef, linkedFields, onEnableField, onDisableField, editMode, componentTask } = useBoundContext()
    const isLinked = !!componentRef && linkedFields?.[FIELD]

    if (!isLinked) {
        const canLink = !!componentRef && componentHasClassification(componentTask)

        return (
            <>
                {originalchildren}
                {editMode && canLink && (
                    <Box sx={unlinkButtonPositionSx}>
                        <Tooltip title="Link classification from component">
                            <IconButton
                                size="small"
                                onClick={preventPropagation(handleEnable)}
                                className="component-link-btn"
                                sx={linkButtonSx}
                                aria-label="Link classification"
                            >
                                <Iconify icon="mdi:puzzle-plus-outline" sx={{ width: 14, height: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </>
        )
    }

    if (componentTask === IS_LOADING) {
        return (
            <Box display="inline-flex" alignItems="center" sx={{ width: 24, height: 24 }}>
                <CircularProgress size={16} />
            </Box>
        )
    }

    if (!componentTask) return originalchildren ?? null

    return (
        <HighlightBox path="classification" display="inline-flex" alignItems="center">
            <Tooltip title={`Inherited from: ${componentTask.heading ?? componentRef}`}>
                <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ opacity: editMode ? 0.6 : 1 }}>
                    <Classification classification={componentTask.classification} />
                    {editMode && (
                        <IconButton
                            size="small"
                            onClick={preventPropagation(handleDisable)}
                            sx={linkedButtonSx}
                            aria-label="Unlink classification"
                        >
                            <Iconify icon="mdi:link-variant" sx={{ width: 14, height: 14 }} />
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
        </HighlightBox>
    )

    function handleEnable() {
        onEnableField?.(FIELD)
    }

    function handleDisable() {
        onDisableField?.(FIELD)
    }
}

function componentHasClassification(task) {
    if (!task || task === IS_LOADING) return false
    return VALID_CLASSIFICATIONS.includes(task.classification)
}
